import styled from 'styled-components';
import InfoTooltip from './InfoTooltip';
import { useEffect, useMemo, useState } from 'react';

type SummaryData = {
  progressed_items: number | string;
  successful_items: number | string;
  success_rate: number | string; // "43.4" 같은 퍼센트 값
  price_success_rate: number | string; // "56.9" 같은 퍼센트 값
  avg_bidders: number | string;
};

type SummaryCardsProps = {
  court: string;
  region: string;
  usage: string;
  usageDetail: string;
  date: { from: Date; to: Date };
  type: 'week' | 'month' | 'year';
  summaryData?: SummaryData | null;
  isLoading?: boolean;
};

type ViewVals = {
  progressed: string;
  successful: string;
  winRate: string;
  winPriceRate: string;
  avgBidders: string;
};

const INFO_LIST = [
  {
    title: '진행건수',
    content: '진행건수는 해당 기간동안 진행 중인 경매 물건의 갯수입니다.',
  },
  {
    title: '낙찰건수',
    content: '진행건수는 해당 기간동안 낙찰된 경매 물건의 갯수입니다.',
  },
  {
    title: '낙찰률',
    content:
      '낙찰률은 해당 기간동안 진행 중인 물건 대비 낙찰된 물건의 비율입니다.',
  },
  {
    title: '낙찰가율',
    content:
      '낙찰가율은 해당 기간동안 낙찰된 물건의 감정가 대비 낙찰 가격의 비율입니다.',
  },
  {
    title: '평균 응찰자 수',
    content:
      '평균 응찰자 수는 해당 기간동안 낙찰된 물건의 입찰한 인원 수 입니다.',
  },
];

const INFO_MAP: Record<string, { title: string; content: string }> =
  Object.fromEntries(
    INFO_LIST.map((i) => [i.title, { title: i.title, content: i.content }]),
  );

export default function SummaryCards({
  court,
  region,
  usage,
  usageDetail,
  date,
  type,
  summaryData,
  isLoading = false,
}: SummaryCardsProps) {
  const [vals, setVals] = useState<ViewVals | null>(null);

  const toNum = (v: unknown) => {
    if (v === null || v === undefined) return 0;
    if (typeof v === 'number') return v;
    const n = Number(String(v).replace(/,/g, ''));
    return isNaN(n) ? 0 : n;
  };

  useEffect(() => {
    if (!summaryData) {
      setVals({
        progressed: '',
        successful: '',
        winRate: '',
        winPriceRate: '',
        avgBidders: '',
      });
      return;
    }
    const progressed_items = toNum(summaryData.progressed_items);
    const successful_items = toNum(summaryData.successful_items);
    const success_rate = toNum(summaryData.success_rate);
    const price_success_rate = toNum(summaryData.price_success_rate);
    const avg_bidders = toNum(summaryData.avg_bidders);

    setVals({
      progressed: progressed_items.toLocaleString('ko-KR'),
      successful: successful_items.toLocaleString('ko-KR'),
      winRate: `${success_rate.toFixed(1)}%`,
      winPriceRate: `${price_success_rate.toFixed(1)}%`,
      avgBidders: avg_bidders.toFixed(1),
    });
  }, [summaryData]);

  const V = useMemo<ViewVals>(() => {
    if (!vals) {
      return {
        progressed: '',
        successful: '',
        winRate: '',
        winPriceRate: '',
        avgBidders: '',
      };
    }
    return vals;
  }, [vals]);

  return (
    <Board>
      <Card>
        <Category>
          <CategoryTitle>
            진행건수
            <InfoTooltip
              title={INFO_MAP['진행건수'].title}
              content={INFO_MAP['진행건수'].content}
            />
          </CategoryTitle>
          <Img $color="#E4EDFD">
            <img src="/assets/images/icon/blue_calendar.svg" />
          </Img>
        </Category>
        <Data aria-live="polite" aria-busy={isLoading}>
          {isLoading ? (
            <LoaderImg src="/assets/images/icon/loader.gif" alt="로딩 중" />
          ) : (
            V.progressed
          )}
        </Data>
      </Card>

      <Card>
        <Category>
          <CategoryTitle>
            낙찰건수
            <InfoTooltip
              title={INFO_MAP['낙찰건수'].title}
              content={INFO_MAP['낙찰건수'].content}
            />
          </CategoryTitle>
          <Img $color="#F0E8FF">
            <img src="/assets/images/icon/purple_hammer.svg" />
          </Img>
        </Category>
        <Data aria-live="polite" aria-busy={isLoading}>
          {isLoading ? (
            <LoaderImg src="/assets/images/icon/loader.gif" alt="로딩 중" />
          ) : (
            V.successful
          )}
        </Data>
      </Card>

      <Card>
        <Category>
          <CategoryTitle>
            낙찰률
            <InfoTooltip
              title={INFO_MAP['낙찰률'].title}
              content={INFO_MAP['낙찰률'].content}
            />
          </CategoryTitle>
          <Img $color="#DCF7E8">
            <img src="/assets/images/icon/green_pie.svg" />
          </Img>
        </Category>
        <Data aria-live="polite" aria-busy={isLoading}>
          {isLoading ? (
            <LoaderImg src="/assets/images/icon/loader.gif" alt="로딩 중" />
          ) : (
            V.winRate
          )}
        </Data>
      </Card>

      <Card>
        <Category>
          <CategoryTitle>
            낙찰가율
            <InfoTooltip
              title={INFO_MAP['낙찰가율'].title}
              content={INFO_MAP['낙찰가율'].content}
            />
          </CategoryTitle>
          <Img $color="#FEEFEF">
            <img src="/assets/images/icon/red_money.svg" />
          </Img>
        </Category>
        <Data aria-live="polite" aria-busy={isLoading}>
          {isLoading ? (
            <LoaderImg src="/assets/images/icon/loader.gif" alt="로딩 중" />
          ) : (
            V.winPriceRate
          )}
        </Data>
      </Card>

      <WideCard>
        <Category>
          <CategoryTitle>
            평균 응찰자 수
            <InfoTooltip
              title={INFO_MAP['평균 응찰자 수'].title}
              content={INFO_MAP['평균 응찰자 수'].content}
            />
          </CategoryTitle>
          <Img $color="#FCEED4">
            <img src="/assets/images/icon/orange_person.svg" />
          </Img>
        </Category>
        <Data aria-live="polite" aria-busy={isLoading}>
          {isLoading ? (
            <LoaderImg src="/assets/images/icon/loader.gif" alt="로딩 중" />
          ) : (
            V.avgBidders
          )}
        </Data>
      </WideCard>
    </Board>
  );
}

const Board = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  align-self: stretch;
`;

const Card = styled.div`
  display: flex;
  padding: 12px 16px;
  flex-direction: column;
  align-items: flex-start;
  flex: 1 0 0;
  border-radius: 8px;
  background: #fff;
  align-self: stretch;
`;

const WideCard = styled(Card)`
  grid-column: 1 / -1;
`;

const Category = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const CategoryTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  color: #424854;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: -0.28px;
`;

const Img = styled.div<{ $color: string }>`
  display: flex;
  padding: 8px;
  align-items: center;
  border-radius: 16px;
  background-color: ${({ $color }) => $color};
  .img {
    width: 16px;
    height: 16px;
  }
`;

const Data = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #0c0e12;
`;

const LoaderImg = styled.img`
  width: 20px;
  height: 20px;
  display: inline-block;
`;
